import { diffLines, type Change } from 'diff';

export type DiffLineKind = 'add' | 'remove' | 'context';

export interface DiffLine {
  kind: DiffLineKind;
  text: string;
}

/**
 * A stretch of the diff. `gap` is a run of unchanged lines far enough from any
 * change to be worth hiding; it carries the lines it hides, so opening it is a
 * render rather than a second pass over the diff.
 */
export interface DiffChunk {
  kind: 'lines' | 'gap';
  lines: DiffLine[];
}

/** Unchanged lines kept on each side of a change, so it is read in context. */
export const CONTEXT_LINES = 3;

/**
 * Below this, hiding costs more than it saves: a "show 1 more line" button is
 * taller than the line it stands in for.
 */
const MIN_HIDDEN = 2;

function splitLines(value: string): string[] {
  if (value === '') return [];
  const body = value.endsWith('\n') ? value.slice(0, -1) : value;
  return body.split('\n');
}

/* By code unit rather than `localeCompare`, so the order never depends on the reader's locale. */
function compareKeys(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

function diffLineKind(part: Change): DiffLineKind {
  if (part.added) return 'add';
  if (part.removed) return 'remove';
  return 'context';
}

/** JSON with its keys in a fixed order, so a re-serialised object does not read as an edit. */
export function stableJson(value: unknown): string {
  return (
    JSON.stringify(
      value,
      (_key, held: unknown) => {
        if (held === null || typeof held !== 'object' || Array.isArray(held)) {
          return held;
        }
        const entries = Object.entries(held as Record<string, unknown>);
        entries.sort(([a], [b]) => compareKeys(a, b));
        return Object.fromEntries(entries);
      },
      2,
    ) ?? ''
  );
}

export function toDiffLines(before: string, after: string): DiffLine[] {
  return diffLines(before, after).flatMap((part) => {
    const kind = diffLineKind(part);
    return splitLines(part.value).map((text) => ({ kind, text }));
  });
}

function pushLines(chunks: DiffChunk[], lines: DiffLine[]): void {
  if (lines.length === 0) return;
  const last = chunks.at(-1);
  if (last?.kind === 'lines') {
    last.lines.push(...lines);
    return;
  }
  chunks.push({ kind: 'lines', lines: [...lines] });
}

/**
 * Folds the long unchanged stretches away, keeping `context` lines on each side
 * of every change.
 *
 * A run at the very start or end is only shown from the side facing the change
 * — nobody reads the top of an unchanged file to understand an edit halfway
 * down. A diff with no changes at all is left whole: there is no change for
 * context to be context to, and folding it would leave a button and nothing else.
 */
export function collapseContext(lines: DiffLine[], context = CONTEXT_LINES): DiffChunk[] {
  if (lines.every((line) => line.kind === 'context')) {
    return lines.length === 0 ? [] : [{ kind: 'lines', lines: [...lines] }];
  }

  const chunks: DiffChunk[] = [];
  let at = 0;

  while (at < lines.length) {
    if (lines[at].kind !== 'context') {
      pushLines(chunks, [lines[at]]);
      at += 1;
      continue;
    }

    let end = at;
    while (end < lines.length && lines[end].kind === 'context') end += 1;

    const run = lines.slice(at, end);
    const atStart = at === 0;
    const atEnd = end === lines.length;
    const keep = atStart || atEnd ? context : context * 2;

    if (run.length - keep < MIN_HIDDEN) {
      pushLines(chunks, run);
    } else if (atStart) {
      chunks.push({ kind: 'gap', lines: run.slice(0, run.length - context) });
      pushLines(chunks, run.slice(run.length - context));
    } else if (atEnd) {
      pushLines(chunks, run.slice(0, context));
      chunks.push({ kind: 'gap', lines: run.slice(context) });
    } else {
      pushLines(chunks, run.slice(0, context));
      chunks.push({ kind: 'gap', lines: run.slice(context, run.length - context) });
      pushLines(chunks, run.slice(run.length - context));
    }

    at = end;
  }

  return chunks;
}
